import { type IntranetChatConversation, type IntranetChatMessage } from '@gb-ticket/shared-types';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  BellOffIcon,
  ImagesIcon,
  Loader2Icon,
  MoreHorizontalIcon,
  PinIcon,
  UsersIcon,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { usePageTitle } from '@/hooks/use-page-title';
import { useVoice, useVoiceCopy } from '@/platform/voice/use-voice';
import { ApiRequestError } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useMe } from '@/platform/auth/use-me';
import { AbsenceNoteStack } from '@/products/intranet/components/absence-note-banner';
import { ChatAttachmentView, ChatComposer } from '@/products/intranet/components/chat-composer';
import { ChatForwardDialog } from '@/products/intranet/components/chat-forward-dialog';
import { ChatLocationBubble } from '@/products/intranet/components/chat-location-map';
import { ChatLocationDialog } from '@/products/intranet/components/chat-location-dialog';
import { ChatPhotoGallery } from '@/products/intranet/components/chat-photo-gallery';
import { CreateGroupChatDialog } from '@/products/intranet/components/create-group-chat-dialog';
import {
  ColleagueNameButton,
  colleagueVisibleName,
} from '@/products/intranet/components/colleague-name-button';
import { ColleaguePortrait } from '@/products/intranet/features/intranet/colleague-portrait';
import {
  fetchIntranetChatMessages,
  intranetChatMessagesKey,
  useForwardIntranetChatMessage,
  useIntranetChatConversations,
  useIntranetChatMessages,
  useIntranetChatSearch,
  useMarkIntranetChatRead,
  useSendIntranetChatMessage,
  useSetIntranetChatPreference,
} from '@/products/intranet/features/intranet/use-intranet-chat';
import { INTRANET_CHAT_PATH, intranetChatPath } from '@/products/intranet/intranet-paths';

export const INTRANET_CHAT_EMPTY_INBOX =
  'Hier erscheinen 1:1-Gespräche mit Kolleginnen und Kollegen ohne Microsoft-Konto sowie Intranet-Gruppen mit mindestens einer lokalen Person. Reine Teams-Gruppen bitte in Microsoft Teams führen.';
export const INTRANET_CHAT_EMPTY_UNREAD = 'Keine ungelesenen Gespräche.';
export const INTRANET_CHAT_EMPTY_GROUPS = 'Keine Gruppen.';
export const INTRANET_CHAT_EMPTY_ARCHIVED = 'Keine archivierten Gespräche.';
export const INTRANET_CHAT_EMPTY_SEARCH = 'Keine Treffer.';
export const INTRANET_CHAT_SEARCH_PENDING = 'Suche …';
export const INTRANET_CHAT_SEARCH_ERROR = 'Die Suche konnte nicht ausgeführt werden.';
export const INTRANET_CHAT_MESSAGE_MISSING = 'Die Nachricht ist nicht mehr im Verlauf.';

type ChatListFilter = 'all' | 'unread' | 'groups' | 'archived';

const CHAT_FILTERS: { id: ChatListFilter; label: string }[] = [
  { id: 'all', label: 'Alle' },
  { id: 'unread', label: 'Ungelesen' },
  { id: 'groups', label: 'Gruppen' },
  { id: 'archived', label: 'Archiviert' },
];

/** Inbox and thread. Under `sm` one pane; from `sm` list and thread side by side. */
export function ChatPage() {
  const { conversationId } = useParams<{ conversationId?: string }>();
  const copy = useVoiceCopy();
  const me = useMe();
  const conversations = useIntranetChatConversations();
  const [groupOpen, setGroupOpen] = useState(false);
  const [filter, setFilter] = useState<ChatListFilter>('all');
  const [search, setSearch] = useState('');
  const active = conversations.data?.items.find((item) => item.id === conversationId);
  const archiveKey = `${conversationId ?? ''}:${active?.archived ? '1' : '0'}`;
  const [seenArchiveKey, setSeenArchiveKey] = useState(archiveKey);
  if (archiveKey !== seenArchiveKey) {
    setSeenArchiveKey(archiveKey);
    if (active?.archived) {
      setFilter('archived');
    }
  }
  const title = active?.title ?? 'Chat';
  usePageTitle(title);

  const items = sortConversations(conversations.data?.items ?? []);
  const query = search.trim();
  const inFilter = items.filter((item) => matchesFilter(item, filter));
  const shown = inFilter.filter((item) => matchesQuery(item, query));
  const messageSearch = useIntranetChatSearch(query);
  const hits = messageSearch.data?.items ?? [];
  const searchingMessages = query.length >= 2;
  const showListOnMobile = !conversationId;
  const showThreadOnMobile = Boolean(conversationId);
  const noHits = !searchingMessages || (!messageSearch.isPending && hits.length === 0);
  const emptyCopy = listEmptyCopy({
    filter,
    inboxEmpty: items.length === 0,
    shownEmpty: shown.length === 0,
    query,
    noHits,
    searchPending: messageSearch.isPending,
  });

  return (
    <section className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
      <CreateGroupChatDialog open={groupOpen} onClose={() => setGroupOpen(false)} />

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden border-border bg-card sm:flex-row">
        <aside
          className={cn(
            'w-full shrink-0 border-border sm:w-80 sm:border-r lg:w-96',
            showListOnMobile ? 'flex min-h-0 flex-col' : 'hidden sm:flex sm:min-h-0 sm:flex-col',
          )}
          aria-label="Gespräche"
        >
          <div className="flex items-center gap-2 px-3 pt-3 pb-2">
            <h1 className="text-lg font-semibold text-foreground">Chats</h1>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="ml-auto"
              onClick={() => setGroupOpen(true)}
            >
              <UsersIcon className="size-4" aria-hidden />
              Neue Gruppe
            </Button>
          </div>
          <div className="px-3 pb-2">
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Suchen …"
              aria-label="Gespräche suchen"
            />
          </div>
          <div className="flex gap-1 overflow-x-auto px-3 pb-2" role="group" aria-label="Filter">
            {CHAT_FILTERS.map((entry) => (
              <Button
                key={entry.id}
                type="button"
                size="sm"
                variant={filter === entry.id ? 'default' : 'secondary'}
                className="rounded-full"
                aria-pressed={filter === entry.id}
                onClick={() => setFilter(entry.id)}
              >
                {entry.label}
              </Button>
            ))}
          </div>

          {conversations.isPending ? (
            <p className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
              <Loader2Icon className="size-4 animate-spin" aria-hidden />
              Gespräche werden geladen …
            </p>
          ) : conversations.isError ? (
            <p role="alert" className="p-4 text-sm text-destructive">
              Die Gespräche konnten nicht geladen werden.
            </p>
          ) : (
            <ul className="min-h-0 flex-1 divide-y divide-border overflow-y-auto">
              {shown.map((item) => (
                <ConversationListItem
                  key={item.id}
                  item={item}
                  active={item.id === conversationId}
                />
              ))}
              {emptyCopy ? (
                <li className="p-4 text-sm text-muted-foreground">{emptyCopy}</li>
              ) : null}
            </ul>
          )}

          {searchingMessages ? (
            <div className="max-h-40 shrink-0 overflow-y-auto border-t border-border bg-card px-3 py-2">
              <p className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
                Nachrichten
              </p>
              {messageSearch.isPending ? (
                <p className="py-2 text-sm text-muted-foreground">{INTRANET_CHAT_SEARCH_PENDING}</p>
              ) : messageSearch.isError ? (
                <p role="alert" className="py-2 text-sm text-destructive">
                  {INTRANET_CHAT_SEARCH_ERROR}
                </p>
              ) : (
                <ul>
                  {hits.map((hit) => (
                    <li key={hit.messageId}>
                      <Link
                        to={`${intranetChatPath(hit.conversationId)}?nachricht=${encodeURIComponent(hit.messageId)}`}
                        className="block py-1.5 text-sm hover:underline"
                      >
                        <span className="font-medium">{hit.conversationTitle}</span>
                        <span className="text-muted-foreground"> — {hit.excerpt}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}
        </aside>

        <div
          className={cn(
            'min-h-0 min-w-0 flex-1 flex-col',
            showThreadOnMobile ? 'flex' : 'hidden sm:flex',
          )}
        >
          {conversationId && me.data ? (
            <ChatThread
              conversationId={conversationId}
              conversation={active ?? null}
              conversations={items}
              myUserId={me.data.id}
              title={active?.title ?? 'Gespräch'}
              subtitle={
                active?.kind === 'GROUP' && active.memberCount
                  ? `${active.memberCount} Personen`
                  : null
              }
            />
          ) : (
            <p className="hidden p-6 text-sm text-muted-foreground sm:block">
              {copy('intranet.chat.pick-conversation')}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

function ConversationListItem({
  item,
  active,
}: {
  item: IntranetChatConversation;
  active: boolean;
}) {
  const preference = useSetIntranetChatPreference(item.id);
  const stamp = chatListStamp(item.lastMessageAt);

  function save(body: {
    pinned?: boolean;
    archived?: boolean;
    muted?: boolean;
    markedUnread?: boolean;
  }) {
    preference.mutate(body, {
      onError: (error) => {
        toast.error(
          error instanceof ApiRequestError
            ? error.message
            : 'Der Chat konnte nicht geändert werden.',
        );
      },
    });
  }

  return (
    <li className={cn('flex items-stretch', active && 'bg-brand-navy-soft/60')}>
      <Link
        to={intranetChatPath(item.id)}
        className="flex min-h-14 min-w-0 flex-1 items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-muted/50"
      >
        <ChatPeoplePreview conversation={item} />
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1">
            <span className="truncate font-medium text-foreground">{item.title}</span>
            {item.pinned ? <PinIcon className="size-3.5 shrink-0" aria-label="Fixiert" /> : null}
            {item.muted ? (
              <BellOffIcon className="size-3.5 shrink-0" aria-label="Stummgeschaltet" />
            ) : null}
            {stamp ? (
              <span className="ml-auto shrink-0 text-xs text-muted-foreground">{stamp}</span>
            ) : null}
            {item.unreadCount > 0 ? (
              <span className="shrink-0 rounded-full bg-brand-navy px-1.5 text-xs text-white">
                {item.unreadCount}
              </span>
            ) : null}
          </span>
          {item.lastMessagePreview ? (
            <span className="block truncate text-sm text-muted-foreground">
              {item.lastMessagePreview}
            </span>
          ) : (
            <span className="block text-sm text-muted-foreground">Noch keine Nachrichten</span>
          )}
        </span>
      </Link>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="my-auto mr-1 size-10 shrink-0"
            aria-label={`Aktionen für ${item.title}`}
          >
            <MoreHorizontalIcon className="size-4" aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => save({ pinned: !item.pinned })}>
            {item.pinned ? 'Loslösen' : 'Fixieren'}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => save({ archived: !item.archived })}>
            {item.archived ? 'Chat wiederherstellen' : 'Chat archivieren'}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => save({ muted: !item.muted })}>
            {item.muted ? 'Stummschaltung aufheben' : 'Chat stummschalten'}
          </DropdownMenuItem>
          {item.unreadCount === 0 ? (
            <DropdownMenuItem onSelect={() => save({ markedUnread: true })}>
              Als ungelesen markieren
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
    </li>
  );
}

function ChatThread({
  conversationId,
  conversation,
  conversations,
  myUserId,
  title,
  subtitle,
}: {
  conversationId: string;
  conversation: IntranetChatConversation | null;
  conversations: IntranetChatConversation[];
  myUserId: string;
  title: string;
  subtitle: string | null;
}) {
  const voice = useVoice();
  const copy = useVoiceCopy();
  const queryClient = useQueryClient();
  const messages = useIntranetChatMessages(conversationId);
  const send = useSendIntranetChatMessage(conversationId);
  const forward = useForwardIntranetChatMessage(conversationId);
  const markRead = useMarkIntranetChatRead(conversationId);
  const [draft, setDraft] = useState('');
  const [reply, setReply] = useState<IntranetChatMessage | null>(null);
  const [forwardMessage, setForwardMessage] = useState<IntranetChatMessage | null>(null);
  const [locationOpen, setLocationOpen] = useState(false);
  const [photosOpen, setPhotosOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const jumping = useRef(false);
  const handledJump = useRef<string | null>(null);
  const [params, setParams] = useSearchParams();
  const items = messages.data?.items ?? [];
  const jumpId = params.get('nachricht');

  useEffect(() => {
    markRead.mutate();
    // Only when the open conversation changes — not on every mutation identity tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open once per conversationId
  }, [conversationId]);

  useEffect(() => {
    if (jumping.current) {
      return;
    }
    const node = listRef.current;
    if (!node) {
      return;
    }
    node.scrollTop = node.scrollHeight;
  }, [items.length, conversationId]);

  useEffect(() => {
    if (!jumpId || !isChatMessageId(jumpId)) {
      return;
    }
    const key = `${conversationId}:${jumpId}`;
    if (handledJump.current === key) {
      return;
    }
    handledJump.current = key;
    jumping.current = true;
    void reveal(jumpId).then((found) => {
      if (!found) {
        toast.error(INTRANET_CHAT_MESSAGE_MISSING);
      }
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete('nachricht');
          return next;
        },
        { replace: true },
      );
      window.setTimeout(() => {
        jumping.current = false;
      }, 0);
    });
    // reveal identity changes with the loaded page; the handled key stops a second jump.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per conversation and message id
  }, [conversationId, jumpId]);

  function chatError(error: unknown, fallback: string) {
    toast.error(error instanceof ApiRequestError ? error.message : fallback);
  }

  async function reveal(messageId: string): Promise<boolean> {
    const key = intranetChatMessagesKey(conversationId);
    const current = queryClient.getQueryData<{
      items: IntranetChatMessage[];
      nextCursor: string | null;
    }>(key);
    let loaded = current?.items ?? items;
    let cursor = current?.nextCursor ?? messages.data?.nextCursor ?? null;
    let guard = 0;
    while (!loaded.some((item) => item.id === messageId) && cursor && guard < 20) {
      const page = await fetchIntranetChatMessages(conversationId, cursor);
      loaded = [...page.items, ...loaded];
      cursor = page.nextCursor;
      guard += 1;
    }
    queryClient.setQueryData(key, { items: loaded, nextCursor: cursor });
    const found = loaded.some((item) => item.id === messageId);
    if (found) {
      window.setTimeout(() => {
        document.getElementById(`chat-msg-${messageId}`)?.scrollIntoView({ block: 'center' });
      }, 0);
    }
    return found;
  }

  async function submitText() {
    const body = draft.trim();
    if (!body || send.isPending) {
      return;
    }
    try {
      await send.mutateAsync({ body, replyToMessageId: reply?.id });
      setDraft('');
      setReply(null);
    } catch (error) {
      chatError(error, 'Die Nachricht konnte nicht gesendet werden.');
    }
  }

  async function submitFile(file: File, voice?: boolean) {
    if (send.isPending) {
      return;
    }
    try {
      await send.mutateAsync({ body: draft.trim(), file, voice, replyToMessageId: reply?.id });
      setDraft('');
      setReply(null);
    } catch (error) {
      chatError(error, 'Die Datei konnte nicht gesendet werden.');
    }
  }

  async function submitLocation(input: { body: string; latitudeE7: number; longitudeE7: number }) {
    if (send.isPending) {
      return;
    }
    try {
      await send.mutateAsync({ ...input, replyToMessageId: reply?.id });
      setDraft('');
      setReply(null);
      setLocationOpen(false);
    } catch (error) {
      chatError(error, 'Der Standort konnte nicht gesendet werden.');
    }
  }

  async function submitForward(input: { targetConversationIds: string[]; comment: string }) {
    if (!forwardMessage || forward.isPending) {
      return;
    }
    try {
      await forward.mutateAsync({
        messageId: forwardMessage.id,
        targetConversationIds: input.targetConversationIds,
        comment: input.comment || undefined,
      });
      setForwardMessage(null);
    } catch (error) {
      chatError(error, 'Die Nachricht konnte nicht weitergeleitet werden.');
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-background">
      <div className="flex min-h-12 items-center gap-2 border-b border-border bg-card px-2 py-2 sm:gap-3 sm:px-4 sm:py-3">
        <Button
          asChild
          variant="ghost"
          size="sm"
          className="h-10 shrink-0 gap-1.5 px-2 text-muted-foreground hover:text-foreground sm:hidden"
        >
          <Link to={INTRANET_CHAT_PATH} aria-label="Zurück zu den Gesprächen">
            <ArrowLeftIcon className="size-4" aria-hidden />
            Zurück
          </Link>
        </Button>
        {conversation?.kind === 'DIRECT' && conversation.other ? (
          <ColleagueNameButton
            userId={conversation.other.id}
            displayName={conversation.other.displayName}
            className="flex min-w-0 flex-1 items-center gap-2 text-left hover:no-underline"
          >
            <ColleaguePortrait
              displayName={conversation.other.displayName}
              profilePhotoUrl={conversation.other.profilePhotoUrl ?? null}
              className="size-10 text-xs"
            />
            <span className="min-w-0">
              <span className="block truncate text-base font-semibold text-primary">{title}</span>
              {subtitle ? (
                <span className="block truncate text-xs text-muted-foreground">{subtitle}</span>
              ) : null}
            </span>
          </ColleagueNameButton>
        ) : (
          <>
            {conversation ? <ChatPeoplePreview conversation={conversation} /> : null}
            <div className="min-w-0 flex-1">
              <h2 className="truncate text-base font-semibold text-primary">{title}</h2>
              {subtitle ? (
                <p className="truncate text-xs text-muted-foreground">{subtitle}</p>
              ) : null}
            </div>
          </>
        )}
        <Button type="button" variant="outline" size="sm" onClick={() => setPhotosOpen(true)}>
          <ImagesIcon className="size-4" aria-hidden />
          Fotos
        </Button>
      </div>

      <AbsenceNoteStack
        className="border-b px-3 py-2 sm:px-4"
        notes={(conversation?.absenceNotes ?? []).map((note) => ({
          id: note.userId,
          title:
            conversation?.kind === 'GROUP' ? `${note.displayName} · ${note.label}` : note.label,
          body: note.body,
        }))}
      />

      <div
        ref={listRef}
        data-testid="chat-message-list"
        className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-3 py-3 sm:gap-3.5 sm:px-4 sm:py-4"
      >
        {messages.isPending ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2Icon className="size-4 animate-spin" aria-hidden />
            Nachrichten werden geladen …
          </p>
        ) : messages.isError ? (
          <p role="alert" className="text-sm text-destructive">
            Die Nachrichten konnten nicht geladen werden.
          </p>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">{copy('intranet.chat.first-message')}</p>
        ) : (
          items.map((message) => (
            <ChatBubble
              key={message.id}
              message={message}
              mine={message.author.id === myUserId}
              onReply={setReply}
              onForward={setForwardMessage}
              onReveal={(messageId) => void reveal(messageId)}
            />
          ))
        )}
      </div>

      <ChatComposer
        draft={draft}
        pending={send.isPending}
        reply={
          reply
            ? {
                authorName: colleagueVisibleName(reply.author.displayName, voice),
                excerpt: replyExcerpt(reply),
              }
            : null
        }
        onCancelReply={() => setReply(null)}
        onDraft={setDraft}
        onSendText={() => void submitText()}
        onSendFile={(file, voice) => void submitFile(file, voice)}
        onOpenLocation={() => setLocationOpen(true)}
      />
      <ChatLocationDialog
        key={locationOpen ? 'location-open' : 'location-closed'}
        open={locationOpen}
        initialBody={draft}
        pending={send.isPending}
        onClose={() => setLocationOpen(false)}
        onSend={(input) => void submitLocation(input)}
      />
      <ChatForwardDialog
        open={forwardMessage !== null}
        conversations={conversations}
        currentId={conversationId}
        pending={forward.isPending}
        onClose={() => setForwardMessage(null)}
        onSubmit={(input) => void submitForward(input)}
      />
      <ChatPhotoGallery
        open={photosOpen}
        conversationId={conversationId}
        onClose={() => setPhotosOpen(false)}
        onReveal={(messageId) => void reveal(messageId)}
      />
    </div>
  );
}

/** Own messages on the right, others on the left. Body is plain text only. */
export function ChatBubble({
  message,
  mine,
  onReply,
  onForward,
  onReveal,
}: {
  message: IntranetChatMessage;
  mine: boolean;
  onReply?: (message: IntranetChatMessage) => void;
  onForward?: (message: IntranetChatMessage) => void;
  onReveal?: (messageId: string) => void;
}) {
  const voice = useVoice();
  return (
    <div
      id={`chat-msg-${message.id}`}
      data-testid={mine ? 'chat-bubble-mine' : 'chat-bubble-theirs'}
      className={cn(
        'flex max-w-[min(85%,24rem)] gap-2 sm:max-w-[75%]',
        mine ? 'ml-auto flex-row-reverse' : 'mr-auto',
      )}
    >
      <div className={cn('min-w-0 space-y-1', mine ? 'text-right' : 'text-left')}>
        <ColleagueNameButton
          userId={message.author.id}
          displayName={message.author.displayName}
          className={cn(
            'inline-flex max-w-full items-center gap-2 hover:no-underline',
            mine && 'flex-row-reverse',
          )}
        >
          <ColleaguePortrait
            displayName={message.author.displayName}
            profilePhotoUrl={message.author.profilePhotoUrl ?? null}
            className="size-8 text-xs"
          />
          <span className="truncate text-xs font-medium">
            {colleagueVisibleName(message.author.displayName, voice)}
          </span>
        </ColleagueNameButton>
        <p
          className={cn(
            'space-y-2 rounded-2xl px-3 py-2 text-sm break-words whitespace-pre-wrap shadow-sm',
            mine ? 'bg-brand-navy text-white' : 'border bg-muted/70 text-foreground',
          )}
        >
          {message.replyTo ? (
            <button
              type="button"
              className="block w-full rounded-md border-l-2 px-2 py-1 text-left text-xs opacity-80"
              onClick={() => onReveal?.(message.replyTo?.id ?? '')}
            >
              <span className="block font-medium">{message.replyTo.authorName}</span>
              <span className="block truncate">{message.replyTo.excerpt}</span>
            </button>
          ) : null}
          {message.forwardedFrom ? (
            <span className="block text-xs">
              Weitergeleitet{' '}
              <ColleagueNameButton
                userId={message.forwardedFrom.id}
                displayName={message.forwardedFrom.displayName}
                className="font-medium"
              />
            </span>
          ) : null}
          {message.latitudeE7 != null && message.longitudeE7 != null ? (
            <ChatLocationBubble latitudeE7={message.latitudeE7} longitudeE7={message.longitudeE7} />
          ) : null}
          {message.attachment ? (
            <ChatAttachmentView attachment={message.attachment} mine={mine} />
          ) : null}
          {message.body ? <span className="block">{message.body}</span> : null}
        </p>
        <div className={cn('flex items-center gap-1', mine && 'flex-row-reverse')}>
          <p className="px-0.5 text-xs text-muted-foreground">
            {formatDateTime(message.createdAt)}
          </p>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-7"
                aria-label="Nachrichtenaktionen"
              >
                <MoreHorizontalIcon className="size-4" aria-hidden />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align={mine ? 'end' : 'start'}>
              <DropdownMenuItem onSelect={() => onReply?.(message)}>Antworten</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => onForward?.(message)}>
                Weiterleiten
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}

function replyExcerpt(message: IntranetChatMessage): string {
  const trimmed = message.body.trim();
  if (trimmed) {
    return trimmed.length <= 80 ? trimmed : `${trimmed.slice(0, 79)}…`;
  }
  if (message.attachment?.kind === 'image') return 'Bild';
  if (message.attachment?.kind === 'video') return 'Video';
  if (message.attachment?.kind === 'file') return 'Datei';
  if (message.attachment?.kind === 'gif') return 'GIF';
  if (message.attachment?.kind === 'voice') return 'Sprachnachricht';
  if (message.latitudeE7 != null) return 'Standort';
  return 'Nachricht';
}

function ChatPeoplePreview({ conversation }: { conversation: IntranetChatConversation }) {
  if (conversation.kind === 'DIRECT' && conversation.other) {
    return (
      <ColleaguePortrait
        displayName={conversation.other.displayName}
        profilePhotoUrl={conversation.other.profilePhotoUrl ?? null}
        className="size-10 text-xs"
      />
    );
  }

  const members = conversation.memberPreview ?? [];
  if (members.length === 0) {
    return (
      <span
        className="grid size-10 shrink-0 place-items-center rounded-full bg-brand-navy-soft text-brand-navy"
        aria-hidden
      >
        <UsersIcon className="size-4" />
      </span>
    );
  }

  return (
    <span className="flex shrink-0" aria-hidden>
      {members.slice(0, 3).map((person, index) => (
        <ColleaguePortrait
          key={person.id}
          displayName={person.displayName}
          profilePhotoUrl={person.profilePhotoUrl ?? null}
          className={cn('size-8 text-[0.65rem] ring-2 ring-card', index > 0 && '-ml-2')}
        />
      ))}
    </span>
  );
}

function matchesFilter(item: IntranetChatConversation, filter: ChatListFilter): boolean {
  if (filter === 'archived') {
    return item.archived;
  }
  if (item.archived) {
    return false;
  }
  if (filter === 'unread') {
    return item.unreadCount > 0;
  }
  if (filter === 'groups') {
    return item.kind === 'GROUP';
  }
  return true;
}

function matchesQuery(item: IntranetChatConversation, query: string): boolean {
  if (query.length === 0) {
    return true;
  }
  const haystack = `${item.title} ${item.lastMessagePreview ?? ''}`.toLocaleLowerCase('de');
  return haystack.includes(query.toLocaleLowerCase('de'));
}

function sortConversations(items: IntranetChatConversation[]): IntranetChatConversation[] {
  return [...items].sort((left, right) => {
    if (left.pinned !== right.pinned) {
      return left.pinned ? -1 : 1;
    }
    if (left.lastMessageAt === right.lastMessageAt) {
      return 0;
    }
    if (!left.lastMessageAt) {
      return 1;
    }
    if (!right.lastMessageAt) {
      return -1;
    }
    return left.lastMessageAt < right.lastMessageAt ? 1 : -1;
  });
}

function listEmptyCopy(input: {
  filter: ChatListFilter;
  inboxEmpty: boolean;
  shownEmpty: boolean;
  query: string;
  noHits: boolean;
  searchPending: boolean;
}): string | null {
  if (!input.shownEmpty) {
    return null;
  }
  if (input.query.length > 0 && input.noHits && !input.searchPending) {
    return INTRANET_CHAT_EMPTY_SEARCH;
  }
  if (input.query.length > 0) {
    return null;
  }
  if (input.filter === 'all' && input.inboxEmpty) {
    return INTRANET_CHAT_EMPTY_INBOX;
  }
  if (input.filter === 'unread') {
    return INTRANET_CHAT_EMPTY_UNREAD;
  }
  if (input.filter === 'groups') {
    return INTRANET_CHAT_EMPTY_GROUPS;
  }
  if (input.filter === 'archived') {
    return INTRANET_CHAT_EMPTY_ARCHIVED;
  }
  return null;
}

function chatListStamp(iso: string | null, now = new Date()): string | null {
  if (!iso) {
    return null;
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  if (date.toDateString() === now.toDateString()) {
    return new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit' }).format(date);
  }
  if (date.getFullYear() === now.getFullYear()) {
    return new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit' }).format(date);
  }
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

const CHAT_MESSAGE_ID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isChatMessageId(value: string): boolean {
  return CHAT_MESSAGE_ID.test(value);
}
